export const DEALER_TYPES = ["Billable", "Internal"] as const;
export const enum DealerType {
  Billable = "Billable",
  Internal = "Internal",
}

export type AddDealer = {
  name: string;
  dba: string;
  addressLine1: string;
  addressLine2: string;
  state: string;
  city: string;
  zipCode: string;
  phone: string;
  website: string;
  dealerType: DealerType;
  isDmpDealer: boolean;
  user: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
};

export enum StaffRole {
  Admin = "admin",
  Staff = "staff",
}

export type User = {
  userId: string;
  idpUserId: string;
  createdAt: string;
  updatedAt: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

export type StaffUser = {
  userId: string;
  user: User;
  staffUserId: string;
  createdAt: string;
  role: StaffRole;
};

export type IntercomModel = {
  intercomModelId: string;
  name: string;
  sku: string;
};

export type Intercom = {
  intercomId: string;
  intercomModelId: string;
  intercomActivationId: string | null;
  intercomModel: string;
  productSku: string;
  propertyOrganization: string;
  dealerOrganization: string;
  serialNumber: string;
  notes: string;
  createdAt: string;
  activatedAt: string;
  deactivatedAt: string;
  authToken: string;
  accessPointId: string | null; // TODO: remove after apps updated
  entryPointId: string | null;
  lastActivatedAt: string;
  cellKitId: string | null;
  cellKitAssignmentId: string | null;
};

export type IntercomComponent = {
  componentId: string;
  intercomActivationId: string;
  componentType: IntercomComponentType;
  connectionStatus: string;
  serialNumber: string | null;
  activatedAt: string | null;
  disabled: boolean;
  metadata: Record<string, unknown>;
};

export enum IntercomComponentType {
  ACCELEROMETER = "ACCELEROMETER",
  CAMERA = "CAMERA",
  EEPROM = "EEPROM",
  ETHERNET = "ETHERNET",
  IO_BOARD = "IO_BOARD",
  KEYPAD = "KEYPAD",
  MAIN_BOARD = "MAIN_BOARD",
  MICROPHONE = "MICROPHONE",
  MODEM = "MODEM",
  READER = "READER",
  SCREEN = "SCREEN",
  SPEAKER = "SPEAKER",
  TEMP_SENSOR = "TEMP_SENSOR",
  TOUCH = "TOUCH",
}

export type CellKitAssignment = {
  activatedAt: string | null;
  activatedByGatehawkUserId: string | null;
  activatedByGatehawkUser?: StaffUser;
  assignedByDealerUserId: string;
  assignedByDealerUser?: DealerUser;
  cellKitAssignmentId: string;
  cellKitId: string;
  createdAt: string;
  deletedAt: string | null;
  intercomActivationId: string;
};

export type CellKit = {
  cellKitId: string;
  serialNumber: string;
  simNumber: string;
  modemId: string;
  notes: string;
  createdAt: string;
  assignments: CellKitAssignment[];
  assignedToIntercomId: string | null;
  carrier: string | null;
};

export type DealerOrganization = {
  dealerOrganizationId: string;
  createdAt: string;
  name: string;
  dba: string;
  displayName: string;
  addressLine1: string;
  addressLine2: string;
  state: string;
  city: string;
  zipCode: string;
  phone: string;
  website: string;
  pointOfContactId: string;
  pointOfContact: DealerUser;
  accountId: string;
  dealerType: DealerType;
  isDmpDealer: boolean;
};

export const PROPERTY_TYPES = ["Billable", "Demo", "Internal"] as const;
export const enum PropertyType {
  Billable = "Billable",
  Demo = "Demo",
  Internal = "Internal",
}

export type PropertyOrganization = {
  propertyOrganizationId: string;
  name: string;
  dealerOrganizationId: string;
  createdAt: Date;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  lat: number;
  lng: number;
  timezoneId: string;
  email: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerAddress: string;
  ownerCity: string;
  ownerState: string;
  ownerZip: string;
  ownerPhone: string;
  ownerEmail: string;
  updatedAt: Date | null;
  contact?: PropertyUser;
  dealer?: DealerOrganization;
  propertyType: PropertyType;
};

export type DealerFinancials = {
  dealerFinancialsId: string;
  dealerOrganizationId: string;
  createdAt: string;
  updatedAt: string;
  businessName: string;
  licenseNumber: string;
  isTaxExempt: boolean;
  taxIdNumber: string;
  billingContactName: string;
  billingContactPhone: string;
  billingContactEmail: string;
  billingContactAddress: string;
  billingContactCity: string;
  billingContactState: string;
  billingContactZipCode: string;
  invoicingMethod: string;
  isUpdated: boolean;
};

export type PaginatedResult<T> = {
  pageSize: number;
  pageNumber: number;
  total: number;
  result: T[];
};

export type DealerUser = {
  dealerUserId: string;
  dealerOrganizationId: string;
  userId: string;

  user: User;
  createdAt: string;
  role: DealerUserRole;
  eulaAcceptedAt: string;
};

export enum DealerUserRole {
  admin = "admin",
  staff = "staff",
}

export type DealerContact = {
  dealerOrganizationId: string;
  dealerUserId: string;
};

export interface ValidationError<T> {
  property: keyof T;
  messages: string[];
}

export enum PropertyUserRole {
  admin = "admin",
  staff = "staff",
}

export type PropertyUser = {
  propertyUserId: string;
  propertyOrganizationId: string;
  userId: string;
  user: User;
  role: PropertyUserRole;
  createdAt: Date;
  eulaAcceptedAt: Date | null;
  updatedAt: Date | null;
};

export enum CellKitCarrier {
  ATT = "AT&T",
  VERIZON = "Verizon",
}

export type FeatureFlags = {
  gen4AdminPortal?: boolean;
};
